import React from 'react';
import styled from 'styled-components/macro';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useOnClickOutside } from 'lib/utils';
import {
  CheckboxInput,
  LoadingIndicator,
  Modal,
  PrimaryButton,
  Search,
} from 'lib/components';
import CloseIcon from 'lib/images/CloseIcon';
import { theme } from 'lib/style';
import {
  fetchCustomerUser,
  fetchCustomerUsers,
  searchCustomers,
} from 'lib/api';
import { useAuth } from 'lib/context';
import ShowLessIcon from 'lib/images/ShowLessIcon';
import ShowMoreIcon from 'lib/images/ShowMoreIcon';
import { mergeDeep } from 'lib/utils/object';
import { DepartmentsCheckboxDropdown } from './DepartmentsCheckboxDropdown';

const Header = styled.div`
  display: flex;
`;

const HeaderTitle = styled.div`
  ${theme.fontBold700}
  font-size: ${theme.fontSizes.lg};
  color: ${theme.palette.themeDark};
`;

const CloseButtonWrap = styled.div`
  margin-left: auto;
  color: ${theme.palette.white};
  height: 24px;
  width: 24px;
  &:hover {
    opacity: 0.7;
    cursor: pointer;
  }
`;

const Content = styled.div`
  width: 944px;
  height: 452px;
  margin-top: 32px;
`;

const Menu = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const SelectionCountWrapper = styled.div`
  display: flex;
  margin-left: 38px;
`;

const SelectionCountText = styled.div`
  font-size: 16px;
  line-height: 24px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.43;
  letter-spacing: normal;
  color: #272a32;
  margin-left: 16px;
  display: flex;
  align-items: center;
`;

const CustomerSelectionCountText = styled.span`
  font-size: 14px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.43;
  letter-spacing: normal;
  color: #9297a2;
  margin-left: 8px;
`;

const UsersList = styled.div`
  margin-top: 32px;
  height: 300px;
  overflow-y: auto;
`;

const UserItem = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 0 8px 48px;
  &:last-child {
    border-bottom: 1px solid #eeeff2;
  }
`;

const CustomerItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 16px 16px 0;
  > div {
    margin-left: 16px;
  }
`;

const CustomerItemTitle = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
`;

const UserTitle = styled.div`
  font-size: 16px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.5;
  letter-spacing: normal;
  color: #272a32;
  margin-left: 32px;
  cursor: pointer;
`;

const Footer = styled.div`
  margin-top: 32px;
  display: flex;
  justify-content: flex-end;
`;

const MyCompanyLabel = styled.div`
  height: 24px;
  padding: 0 8px;
  border-radius: 4px;
  background-color: #eeeff2;

  font-style: normal;
  font-weight: 600;
  font-size: 12px;
  line-height: 24px;
  text-transform: uppercase;
  color: #4e5461;
  margin-left: 8px;
`;

const NoResults = styled.div`
  width: 100%;
  height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 32px;
  background-color: #f6f7f9;
`;

const CHECKBOX_COLOR = 'rgba(0, 27, 83, 0.6)';

type Props = {
  initUsers?: string[];
  handleModalClose: (reportId?: string) => void;
  onSelect: (userIds: string[]) => void;
};

type Selection = {
  [key: string]: { [key: string]: boolean };
};

export const CustomersSelectorModal = (props: Props) => {
  const {
    userData: { customerId },
  } = useAuth();
  const [loaded, setLoaded] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [customers, setCustomers] = React.useState({} as any);
  const [customersCount, setCount] = React.useState(0);
  const [page, setPage] = React.useState(0);
  const [users, setUsers] = React.useState({} as any);
  const [usersMap, setUsersMap] = React.useState({} as any);
  const [filteredUsers, setFilteredUsers] = React.useState({} as any);
  const [selectedUsers, setSelectedUsers] = React.useState({} as Selection);
  const [selectAll, setSelectAll] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [selectedCustomers, setSelectedCustomers] = React.useState({} as any);
  const [departmentsMap, setDepartmentsMap] = React.useState({} as any);

  const { handleModalClose, initUsers = [], onSelect } = props;
  const { ref } = useOnClickOutside(handleModalClose);

  const escPress = (key: string) => {
    if (key === 'Escape') {
      handleModalClose();
    }
  };

  React.useEffect(() => {
    document.addEventListener('keyup', e => escPress(e.key));

    return () =>
      document.removeEventListener('keyup', e => escPress(e.key), true);
  }, []);

  const loadUsers = async () => {
    const selected = { ...selectedUsers };

    await Promise.all(
      initUsers.map(async u => {
        const user = await fetchCustomerUser(u);
        if (!selected[user.customerId]) {
          selected[user.customerId] = {};
        }
        selected[user.customerId][user.id] = true;
      })
    );

    setSelectedUsers(selected);
  };

  React.useEffect(() => {
    if (initUsers && initUsers.length) {
      loadUsers();
    }
  }, [initUsers]);

  const fetchCustomers = async (
    p: number = page,
    limit: number = 10
  ): Promise<any[]> => {
    const [_customers, count] = await searchCustomers(
      searchQuery,
      false,
      p,
      limit
    );
    setPage(p + 1);
    setCount(count);

    let uM = { ...usersMap };
    let cstmrs = { ...customers };
    let sC = { ...selectedCustomers };
    let sU = { ...selectedUsers };
    let usrs = { ...users };
    let fU = { ...filteredUsers };
    let depart = { ...departmentsMap };

    return new Promise(async resolve => {
      await Promise.all(
        _customers.map(async (c: any) => {
          const _users = await fetchCustomerUsers(c.customerId);
          _users.sort((userA: any, userB: any) => {
            if (userA.firstName < userB.firstName) {
              return -1;
            }
            if (userA.firstName > userB.firstName) {
              return 1;
            }
            return 0;
          });

          const deptUsed = {};
          const uMap = _users.reduce((obj: any, u: any) => {
            obj[u.id] = c.customerId;

            if (u.DeptId && u.Dept) {
              if (!depart[c.customerId]) {
                depart[c.customerId] = [];
              }
              // @ts-ignore
              if (!deptUsed[u.DeptId]) {
                depart[c.customerId].push({
                  value: u.DeptId,
                  label: u.Dept,
                });
                // @ts-ignore
                deptUsed[u.DeptId] = true;
              }
            }
            return obj;
          }, {});

          (depart[c.customerId] || []).sort(
            (departmentA: any, departmentB: any) => {
              if (
                departmentA.label.toLowerCase() <
                departmentB.label.toLowerCase()
              ) {
                return -1;
              }
              if (
                departmentA.label.toLowerCase() >
                departmentB.label.toLowerCase()
              ) {
                return 1;
              }
              return 0;
            }
          );

          uM = { ...uM, ...uMap };
          cstmrs[c.customerId] = c;
          sC[c.customerId] = false;
          sU[c.customerId] = sU[c.customerId] || {};
          usrs[c.customerId] = _users;
          fU[c.customerId] = _users;
        })
      );

      setUsersMap(uM);
      setCustomers(cstmrs);
      setSelectedCustomers(sC);
      setSelectedUsers(selected => mergeDeep(selected, sU) as Selection);
      setUsers(usrs);
      setFilteredUsers(fU);
      setLoaded(true);
      setDepartmentsMap(depart);

      resolve([fU, usrs]);
    });
  };

  const searchForCustomers = async (query: string = '') => {
    query = query.toLowerCase();
    setSearchQuery(query);
    const [cst, cnt] = await searchCustomers(query);
    setCount(cnt);

    let uM = { ...usersMap };
    let cstmrs = { ...customers };
    let sC = { ...selectedCustomers };
    let sU = { ...selectedUsers };
    let usrs = { ...users };
    let fU: any = {};

    return new Promise(async resolve => {
      await Promise.all(
        cst.map(async (c: any) => {
          const _users = await fetchCustomerUsers(c.customerId);
          _users.sort((userA: any, userB: any) => {
            if (userA.firstName < userB.firstName) {
              return -1;
            }
            if (userA.firstName > userB.firstName) {
              return 1;
            }
            return 0;
          });

          const uMap = _users.reduce((obj: any, u: any) => {
            obj[u.id] = c.customerId;
            return obj;
          }, {});

          uM = { ...uM, ...uMap };
          cstmrs[c.customerId] = c;
          sC[c.customerId] = false;
          sU[c.customerId] = sU[c.customerId] || {};
          usrs[c.customerId] = _users;
          fU[c.customerId] = _users;
        })
      );

      setUsersMap(uM);
      setCustomers(cstmrs);
      setSelectedCustomers(sC);
      setSelectedUsers(sU);
      setUsers(usrs);
      setFilteredUsers(fU);
      setLoaded(true);

      resolve([fU, usrs]);
    });
  };

  React.useEffect(() => {
    fetchCustomers();
  }, []);

  const toggleUser = (userId: string, customerId: string) => {
    const selected = { ...selectedUsers };

    if (selected[customerId] && selected[customerId][userId]) {
      delete selected[customerId][userId];
    } else {
      selected[customerId][userId] = true;
    }
    setSelectedUsers(selected);
  };

  const toggleCustomer = (customerId: string) => {
    const selected = { ...selectedUsers };

    if (Object.values(selected[customerId]).length) {
      selected[customerId] = {};
    } else {
      selected[customerId] = users[customerId].reduce((obj: any, u: any) => {
        obj[u.id] = true;
        return obj;
      }, {});
    }
    setSelectedUsers(selected);
  };

  const toggleSelectAll = async () => {
    setLoading(true);
    const [fU, usrs] = await fetchCustomers(0, 500);
    const value = !selectAll;
    const selected = { ...selectedUsers };
    if (value) {
      Object.keys(fU).forEach((c: any) => {
        selected[c] = usrs[c].reduce((obj: any, u: any) => {
          obj[u.id] = true;
          return obj;
        }, {});
      });
    } else {
      Object.keys(fU).forEach((c: any) => {
        selected[c] = {};
      });
    }
    setSelectAll(value);
    setSelectedUsers(selected);
    setLoading(false);
  };

  const companiesCount = Object.values(selectedUsers).reduce(
    (sum, usrs) => sum + (Object.keys(usrs).length ? 1 : 0),
    0
  );
  const usersCount = Object.values(selectedUsers).reduce(
    (sum, usrs) => sum + Object.keys(usrs).length,
    0
  );
  const searchResults = Object.values(filteredUsers).reduce(
    (sum: number, usrs: any) => sum + (Object.keys(usrs).length ? 1 : 0),
    0
  );

  let selectionText = `Selected: ${usersCount} users `;
  if (companiesCount) {
    selectionText += `in ${companiesCount} ${
      companiesCount > 1 ? 'companies' : 'company'
    }`;
  }

  return (
    <Modal style={{ position: 'fixed' }}>
      <div ref={ref} style={{ padding: '32px' }}>
        <Header>
          <HeaderTitle>New Custom Report</HeaderTitle>
          <CloseButtonWrap title={'Close'}>
            <CloseIcon
              onClick={() => handleModalClose()}
              width={24}
              height={24}
              color={theme.palette.label}
            />
          </CloseButtonWrap>
        </Header>
        <Content>
          <>
            <Menu>
              <SelectionCountWrapper>
                <CheckboxInput checked={selectAll} onChange={toggleSelectAll} />
                <SelectionCountText>{selectionText}</SelectionCountText>
              </SelectionCountWrapper>
              <div style={{ width: '300px', marginRight: '30px' }}>
                <Search
                  placeholder='Search companies...'
                  onSearch={searchForCustomers}
                />
              </div>
            </Menu>
            {loading && <LoadingIndicator isLoading={loading} height='332px' />}

            {!loading && !!searchResults && (
              <UsersList>
                <InfiniteScroll
                  dataLength={Object.keys(filteredUsers).length}
                  next={fetchCustomers}
                  hasMore={customersCount > Object.keys(filteredUsers).length}
                  loader={<h4>Loading...</h4>}
                  height={300}
                  endMessage={
                    <p style={{ textAlign: 'center', fontSize: '14px' }}>
                      <b>No more companies</b>
                    </p>
                  }
                >
                  {Object.keys(filteredUsers).map((c: any) => (
                    <div key={c}>
                      <CustomerItem>
                        <CustomerItemTitle
                          onClick={() =>
                            setSelectedCustomers((cs: any) => ({
                              ...cs,
                              [c]: !selectedCustomers[c],
                            }))
                          }
                        >
                          {!selectedCustomers[c] && (
                            <ShowMoreIcon color={CHECKBOX_COLOR} />
                          )}
                          {selectedCustomers[c] && (
                            <ShowLessIcon color={CHECKBOX_COLOR} />
                          )}
                          <CheckboxInput
                            checked={
                              selectedUsers[c] &&
                              !!Object.values(selectedUsers[c]).length
                            }
                            onChange={() => {
                              toggleCustomer(c);
                            }}
                          />
                          <span style={{ marginLeft: '8px' }}>
                            {customers[c] && customers[c].business}
                          </span>

                          <CustomerSelectionCountText>
                            (
                            {selectedUsers[c]
                              ? Object.keys(selectedUsers[c]).length
                              : 0}
                            /{Object.keys(users[c]).length})
                          </CustomerSelectionCountText>

                          {c === customerId && (
                            <MyCompanyLabel>My Company</MyCompanyLabel>
                          )}
                        </CustomerItemTitle>

                        <DepartmentsCheckboxDropdown
                          departments={departmentsMap[c]}
                          menuPortalTarget={null}
                          menuShouldBlockScroll={false}
                          onDepartmentsSelected={departments => {
                            if (filteredUsers[c] && filteredUsers[c].length) {
                              const filtered = { ...users };
                              if (departments.length) {
                                filtered[c] = filtered[c].filter((user: any) =>
                                  departments.includes(user.DeptId)
                                );
                              }
                              setFilteredUsers(filtered);
                            }
                          }}
                        />
                      </CustomerItem>

                      {selectedCustomers[c] &&
                        filteredUsers[c].map((user: any) => (
                          <UserItem key={user.id}>
                            <CheckboxInput
                              checked={!!selectedUsers[c][user.id]}
                              onChange={() => {
                                toggleUser(user.id, c);
                              }}
                            />

                            <UserTitle
                              onClick={() => {
                                toggleUser(user.id, c);
                              }}
                            >
                              {`${user.firstName} ${user.lastName}`}
                            </UserTitle>
                          </UserItem>
                        ))}
                    </div>
                  ))}
                </InfiniteScroll>
              </UsersList>
            )}
            {!loading && loaded && !searchResults && (
              <NoResults>Your search didn’t match any company</NoResults>
            )}
            <Footer>
              <PrimaryButton
                text='Continue'
                textColor='white'
                borderRadius={5}
                fontSize='15px'
                textPadding='8px 16px'
                onClick={() => {
                  onSelect(
                    Object.keys(selectedUsers)
                      .map(c => Object.keys(selectedUsers[c]))
                      .flat()
                  );
                }}
                disabled={!usersCount}
              />
            </Footer>
          </>
        </Content>
      </div>
    </Modal>
  );
};
